import { memo, type PointerEventHandler, type ReactNode } from "react";
import type { FollowUpDelivery, RuntimeMode } from "@threadlines/contracts";
import {
  ChevronDownIcon,
  CornerDownRightIcon,
  ListEndIcon,
  MessageCircleQuestionIcon,
  SquareIcon,
} from "lucide-react";
import { cn } from "~/lib/utils";
import { Button } from "../ui/button";
import {
  Menu,
  MenuItem,
  MenuPopup,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSeparator,
  MenuTrigger,
} from "../ui/menu";
import type { RuntimeModeOption } from "../../runtimeModeOptions";

interface ComposerPrimaryActionsProps {
  compact: boolean;
  isRunning: boolean;
  showPlanFollowUpPrompt: boolean;
  promptHasText: boolean;
  isSendBusy: boolean;
  isConnecting: boolean;
  isEnvironmentUnavailable: boolean;
  isPreparingWorktree: boolean;
  hasSendableContent: boolean;
  preserveComposerFocusOnPointerDown?: boolean;
  runtimeMode: RuntimeMode;
  runtimeModeOptions: ReadonlyArray<RuntimeModeOption>;
  onRuntimeModeChange: (mode: RuntimeMode) => void;
  followUpDelivery: FollowUpDelivery;
  onFollowUpDeliveryChange: (delivery: FollowUpDelivery) => void;
  /** In a room, while another agent works and this message is for a different one. */
  roomDelivery?: ComposerRoomDelivery | null;
  /** Picks, for this message only, between asking now and waiting. */
  onRoomDeliveryChange?: (delivery: FollowUpDelivery) => void;
  onInterrupt: () => void;
  onImplementPlanInNewThread: () => void;
}

/** What the send button says it will do with a message typed mid-turn. */
export const FOLLOW_UP_DELIVERY_LABELS: Record<
  FollowUpDelivery,
  { readonly action: string; readonly tooltip: string; readonly menuLabel: string }
> = {
  steer: { action: "Steer active turn", tooltip: "Steer", menuLabel: "Steer now" },
  queue: {
    action: "Send when this reply finishes",
    tooltip: "Send when done",
    menuLabel: "Send when done",
  },
};

/**
 * A message for one room agent while another works: asked now, answered
 * read-only on the side, or sent once the agent at work finishes. The mode
 * comes from `resolveRoomDelivery`: asking now unless waiting was picked for
 * this message.
 */
export interface ComposerRoomDelivery {
  readonly mode: "ask" | "queue";
  /** False when the picked agent's provider cannot answer on the side. */
  readonly canAsk: boolean;
  readonly recipientName: string;
  readonly holderName: string;
}

export const roomDeliveryLabels = (delivery: ComposerRoomDelivery) =>
  delivery.mode === "ask"
    ? { action: `Ask ${delivery.recipientName} now`, tooltip: "Ask now" }
    : {
        action: `Send to ${delivery.recipientName} when ${delivery.holderName} finishes`,
        tooltip: "Send when done",
      };

export const formatPendingPrimaryActionLabel = (input: {
  compact: boolean;
  isLastQuestion: boolean;
  isResponding: boolean;
  questionIndex: number;
}) => {
  if (input.isResponding) {
    return "Submitting...";
  }
  if (input.compact) {
    return input.isLastQuestion ? "Submit" : "Next";
  }
  if (!input.isLastQuestion) {
    return "Next question";
  }
  return input.questionIndex > 0 ? "Submit answers" : "Submit answer";
};

const preventPointerFocus: PointerEventHandler<HTMLElement> = (event) => {
  event.preventDefault();
};

/** Shared by the normal toolbar and the approval toolbar. */
export const ComposerStopButton = memo(function ComposerStopButton({
  onInterrupt,
  preserveComposerFocusOnPointerDown = false,
}: {
  onInterrupt: () => void;
  preserveComposerFocusOnPointerDown?: boolean;
}) {
  return (
    <Button
      type="button"
      size="icon"
      variant="destructive"
      className="rounded-full before:hidden"
      onPointerDown={preserveComposerFocusOnPointerDown ? preventPointerFocus : undefined}
      onClick={onInterrupt}
      aria-label="Stop generation"
      tooltip="Stop"
    >
      <SquareIcon className="size-2.5 fill-current" strokeWidth={0} />
    </Button>
  );
});

/** Send button with a menu choosing how a message typed mid-turn goes out. */
function FollowUpSplitButton(props: {
  /** Which way the button sends now, for tests and styling. */
  mode: string;
  action: string;
  tooltip: string;
  icon: ReactNode;
  value: FollowUpDelivery;
  options: ReadonlyArray<{
    value: FollowUpDelivery;
    label: string;
    title: string;
    disabled?: boolean;
  }>;
  onChange: (delivery: FollowUpDelivery) => void;
  disabled: boolean;
  pointerFocusProps: { onPointerDown: PointerEventHandler<HTMLElement> } | undefined;
}) {
  return (
    <div
      className="flex items-center"
      data-chat-composer-follow-up-actions="true"
      data-follow-up-mode={props.mode}
    >
      <Button
        type="submit"
        size="icon"
        className="rounded-l-full rounded-r-none"
        {...props.pointerFocusProps}
        disabled={props.disabled}
        aria-label={props.action}
        tooltip={props.tooltip}
      >
        {props.icon}
      </Button>
      <Menu>
        <MenuTrigger
          render={
            <Button
              size="icon"
              variant="default"
              className="w-5 rounded-l-none rounded-r-full border-l-white/12 px-0"
              aria-label="Choose how this message is sent"
              {...props.pointerFocusProps}
              disabled={props.disabled}
            />
          }
        >
          <ChevronDownIcon className="size-3" />
        </MenuTrigger>
        <MenuPopup align="end" side="top">
          <MenuRadioGroup
            value={props.value}
            onValueChange={(value) => {
              if (value === "steer" || value === "queue") {
                props.onChange(value);
              }
            }}
          >
            {props.options.map((option) => (
              <MenuRadioItem
                key={option.value}
                value={option.value}
                disabled={option.disabled === true}
                title={option.title}
              >
                {option.label}
              </MenuRadioItem>
            ))}
          </MenuRadioGroup>
        </MenuPopup>
      </Menu>
    </div>
  );
}

export const ComposerPrimaryActions = memo(function ComposerPrimaryActions({
  compact,
  isRunning,
  showPlanFollowUpPrompt,
  promptHasText,
  isSendBusy,
  isConnecting,
  isEnvironmentUnavailable,
  isPreparingWorktree,
  hasSendableContent,
  preserveComposerFocusOnPointerDown = false,
  runtimeMode,
  runtimeModeOptions,
  onRuntimeModeChange,
  followUpDelivery,
  onFollowUpDeliveryChange,
  roomDelivery = null,
  onRoomDeliveryChange,
  onInterrupt,
  onImplementPlanInNewThread,
}: ComposerPrimaryActionsProps) {
  const pointerFocusProps = preserveComposerFocusOnPointerDown
    ? { onPointerDown: preventPointerFocus }
    : undefined;

  // Also while the agent at work only waits on background work, when there
  // is no turn to steer or stop.
  if (roomDelivery !== null && hasSendableContent) {
    const labels = roomDeliveryLabels(roomDelivery);
    return (
      <div className="flex shrink-0 items-center gap-1.5">
        <FollowUpSplitButton
          mode={roomDelivery.mode}
          action={labels.action}
          tooltip={labels.tooltip}
          icon={
            roomDelivery.mode === "queue" ? (
              <ListEndIcon className="size-3.5" />
            ) : (
              <MessageCircleQuestionIcon className="size-3.5" />
            )
          }
          value={roomDelivery.mode === "ask" ? "steer" : "queue"}
          options={[
            {
              value: "steer",
              label: "Ask now",
              disabled: !roomDelivery.canAsk,
              title: roomDelivery.canAsk
                ? `${roomDelivery.recipientName} answers now and can only read, while ${roomDelivery.holderName} keeps working`
                : `${roomDelivery.recipientName} can't answer while another agent works`,
            },
            {
              value: "queue",
              label: `Send when ${roomDelivery.holderName} finishes`,
              title: `Send to ${roomDelivery.recipientName} when ${roomDelivery.holderName} finishes`,
            },
          ]}
          onChange={(delivery) => onRoomDeliveryChange?.(delivery)}
          disabled={isSendBusy || isConnecting || isEnvironmentUnavailable}
          pointerFocusProps={pointerFocusProps}
        />
      </div>
    );
  }

  if (isRunning) {
    return (
      <div className="flex shrink-0 items-center gap-1.5">
        {hasSendableContent ? (
          <FollowUpSplitButton
            mode={followUpDelivery}
            action={FOLLOW_UP_DELIVERY_LABELS[followUpDelivery].action}
            tooltip={FOLLOW_UP_DELIVERY_LABELS[followUpDelivery].tooltip}
            icon={
              followUpDelivery === "queue" ? (
                <ListEndIcon className="size-3.5" />
              ) : (
                <CornerDownRightIcon className="size-3.5" />
              )
            }
            value={followUpDelivery}
            options={[
              {
                value: "steer",
                label: FOLLOW_UP_DELIVERY_LABELS.steer.menuLabel,
                title: "Add this message to the reply in progress",
              },
              {
                value: "queue",
                label: FOLLOW_UP_DELIVERY_LABELS.queue.menuLabel,
                title: "Wait for the reply to finish, then send",
              },
            ]}
            onChange={onFollowUpDeliveryChange}
            disabled={isSendBusy || isConnecting || isEnvironmentUnavailable}
            pointerFocusProps={pointerFocusProps}
          />
        ) : (
          <ComposerStopButton
            onInterrupt={onInterrupt}
            preserveComposerFocusOnPointerDown={preserveComposerFocusOnPointerDown}
          />
        )}
      </div>
    );
  }

  if (showPlanFollowUpPrompt) {
    if (promptHasText) {
      return (
        <Button
          type="submit"
          size="sm"
          className={cn("rounded-full", compact ? "h-9 px-3 sm:h-8" : "h-9 px-4 sm:h-8")}
          {...pointerFocusProps}
          disabled={isSendBusy || isConnecting || isEnvironmentUnavailable}
        >
          {isConnecting || isSendBusy ? "Sending..." : "Refine"}
        </Button>
      );
    }

    return (
      <div data-chat-composer-implement-actions="true" className="flex items-center justify-end">
        <Button
          type="submit"
          size="sm"
          className="h-9 rounded-l-full rounded-r-none px-4 sm:h-8"
          {...pointerFocusProps}
          disabled={isSendBusy || isConnecting || isEnvironmentUnavailable}
        >
          {isConnecting || isSendBusy ? "Sending..." : "Implement"}
        </Button>
        <Menu>
          <MenuTrigger
            render={
              <Button
                size="sm"
                variant="default"
                className="h-9 rounded-l-none rounded-r-full border-l-white/12 px-2 sm:h-8"
                aria-label="Implementation actions"
                {...pointerFocusProps}
                disabled={isSendBusy || isConnecting || isEnvironmentUnavailable}
              />
            }
          >
            <ChevronDownIcon className="size-3.5" />
          </MenuTrigger>
          <MenuPopup align="end" side="top">
            <MenuItem
              disabled={isSendBusy || isConnecting || isEnvironmentUnavailable}
              onClick={() => void onImplementPlanInNewThread()}
            >
              Implement in a new thread
            </MenuItem>
            {runtimeModeOptions.length > 1 ? (
              <>
                <MenuSeparator />
                <div className="px-2 py-1.5 font-medium text-muted-foreground text-xs">
                  Implement using
                </div>
                <MenuRadioGroup
                  value={runtimeMode}
                  onValueChange={(value) => {
                    if (!value || value === runtimeMode) return;
                    onRuntimeModeChange(value as RuntimeMode);
                  }}
                >
                  {runtimeModeOptions.map((option) => (
                    <MenuRadioItem
                      key={option.mode}
                      value={option.mode}
                      disabled={option.disabled === true}
                      title={
                        option.disabled && option.disabledReason
                          ? option.disabledReason
                          : option.description
                      }
                    >
                      {option.label}
                    </MenuRadioItem>
                  ))}
                </MenuRadioGroup>
              </>
            ) : null}
          </MenuPopup>
        </Menu>
      </div>
    );
  }

  return (
    <button
      type="submit"
      className="flex h-9 w-9 enabled:cursor-pointer items-center justify-center rounded-full bg-primary/90 text-primary-foreground transition-all duration-150 hover:bg-primary hover:scale-105 disabled:pointer-events-none disabled:opacity-30 disabled:hover:scale-100 sm:h-8 sm:w-8"
      {...pointerFocusProps}
      disabled={isSendBusy || isConnecting || isEnvironmentUnavailable || !hasSendableContent}
      aria-label={
        isEnvironmentUnavailable
          ? "Environment disconnected"
          : isConnecting
            ? "Connecting"
            : isPreparingWorktree
              ? "Preparing worktree"
              : isSendBusy
                ? "Sending"
                : "Send message"
      }
    >
      {isConnecting || isSendBusy ? (
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          className="animate-spin"
          aria-hidden="true"
        >
          <circle
            cx="7"
            cy="7"
            r="5.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeDasharray="20 12"
          />
        </svg>
      ) : (
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
          <path
            d="M7 11.5V2.5M7 2.5L3 6.5M7 2.5L11 6.5"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      )}
    </button>
  );
});
